import {
  comprimento,
  cross,
  dot,
  girarEmTorno,
  grausParaRad,
  limitar,
  multiplicar,
  normalizar,
  projetarNoPlano,
  radParaGraus,
  somar,
  subtrair,
  type Vetor3,
} from '../geometria/vetor3'
import { referenciaisDaColuna, type EixoColuna } from '../geometria/anguloExtracao'
import type { RotaExtracao } from './rotasExtracao'

/**
 * Orientação da seta cervical interativa (P3.2).
 *
 * Guardada em coordenadas ANATÔMICAS — azimute em torno do eixo da coluna e
 * elevação a partir dele — em vez de um vetor de mundo bruto. Isso é o que
 * permite arrastar a ponta na cena 3D e mexer nos sliders do painel serem a
 * MESMA operação sobre o MESMO estado: os dois só escrevem `OrientacaoSeta`.
 */
export interface OrientacaoSeta {
  /** ângulo no plano transversal, a partir do eixo sagital; mesma convenção de `calcularAnguloExtracao` */
  azimuteGraus: number
  /** ângulo entre a seta e o eixo da coluna; 0 = seta alinhada ao eixo */
  elevacaoGraus: number
}

export const SETA_NEUTRA: OrientacaoSeta = { azimuteGraus: 0, elevacaoGraus: 0 }

/** Direção de mundo da seta, dada a orientação anatômica. */
export function direcaoDaSeta(eixoColuna: EixoColuna, orientacao: OrientacaoSeta): Vetor3 {
  const { eixo, eixoSagital } = referenciaisDaColuna(eixoColuna)
  const transverso = girarEmTorno(eixoSagital, eixo, grausParaRad(orientacao.azimuteGraus))
  const elevacaoRad = grausParaRad(orientacao.elevacaoGraus)
  return normalizar(
    somar(multiplicar(eixo, Math.cos(elevacaoRad)), multiplicar(transverso, Math.sin(elevacaoRad))),
  )
}

/**
 * Orientação anatômica equivalente a uma direção de mundo — o inverso de
 * `direcaoDaSeta`. É o que traduz o ponto que o aluno arrasta na cena de volta
 * para o estado guardado.
 */
export function orientacaoDaDirecao(eixoColuna: EixoColuna, direcao: Vetor3): OrientacaoSeta {
  const { eixo, eixoSagital } = referenciaisDaColuna(eixoColuna)
  const d = normalizar(direcao)
  const elevacaoGraus = radParaGraus(Math.acos(limitar(dot(d, eixo), -1, 1)))

  const projecao = projetarNoPlano(d, eixo)
  if (comprimento(projecao) < 1e-6) return { azimuteGraus: 0, elevacaoGraus }

  const direcaoTransversa = normalizar(projecao)
  const anguloAzimute = radParaGraus(Math.acos(limitar(dot(eixoSagital, direcaoTransversa), -1, 1)))
  const sinal = dot(cross(eixoSagital, direcaoTransversa), eixo) >= 0 ? 1 : -1

  return { azimuteGraus: anguloAzimute * sinal, elevacaoGraus }
}

/** Alinha a seta com a direção de saída de uma rota já calculada. */
export function orientacaoDaRota(eixoColuna: EixoColuna, rota: RotaExtracao): OrientacaoSeta {
  return orientacaoDaDirecao(eixoColuna, subtrair(rota.pontoSaida, eixoColuna.baseColuna))
}
